import { Route, Routes, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from './components/Layout'
import AuthGuard from './components/AuthGuard'
import Landing from './pages/Landing'
import LoginPage from './pages/Login'
import SessionsPage from './pages/Sessions'
import CreateSessionPage from './pages/CreateSession'
import SessionDetailPage from './pages/SessionDetail'
import CostBreakdownPage from './pages/CostBreakdown'
import ClassesPage from './pages/Classes'
import SubscribersPage from './pages/Subscribers'
import SessionSetupPage from './pages/SessionSetup'
import MembersPage from './pages/Members'
import AnalyticsPage from './pages/Analytics'
import LLMSettingsPage from './pages/LLMSettings'
import AdminStatsPage from './pages/AdminStats'
import AdminTestPage from './pages/AdminTest'
import AdminPackagesPage from './pages/AdminPackages'
import ProfilePage from './pages/Profile'
import ExamCCreatePage from './pages/ExamCCreate'
import ExamCSetupPage from './pages/ExamCSetup'
import ExamDCreatePage from './pages/ExamDCreate'
import LabPage from './pages/Lab'
import { OrgProvider } from './state/OrgContext'

/* Mobile PWA imports */
import { MobileShell, MobileTabs } from './mobile/MobileLayout'
import MExamList from './mobile/pages/MExamList'
import MExamCreate from './mobile/pages/MExamCreate'
import MExamSetup from './mobile/pages/MExamSetup'
import MGradeReview from './mobile/pages/MGradeReview'
import MProfile from './mobile/pages/MProfile'

/* Demo imports */
import { DemoProvider } from './demo/DemoContext'
import { DemoLayout } from './demo/DemoLayout'
import DemoExams from './demo/pages/DemoExams'
import DemoCreateExam from './demo/pages/DemoCreateExam'
import DemoExamDetail from './demo/pages/DemoExamDetail'
import DemoExamSetup from './demo/pages/DemoExamSetup'
import DemoAnalytics from './demo/pages/DemoAnalytics'
import DemoClasses from './demo/pages/DemoClasses'
import DemoMembers from './demo/pages/DemoMembers'
import DemoCosts from './demo/pages/DemoCosts'

function SyncDocumentTitle() {
  const { t, i18n } = useTranslation('common')
  useEffect(() => {
    const name = t('appName')
    document.title = name
    document
      .querySelector('meta[name="apple-mobile-web-app-title"]')
      ?.setAttribute('content', name)
  }, [t, i18n.language])
  return null
}

function App() {
  return (
    <>
      <SyncDocumentTitle />
      <Routes>
        {/* Landing — no sidebar/header */}
        <Route path="/" element={<Landing />} />
        <Route path="/app" element={<Landing />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Demo routes — self-contained layout, no real auth/API */}
        <Route
          path="/demo"
          element={
            <DemoProvider>
              <DemoLayout />
            </DemoProvider>
          }
        >
          <Route index element={<Navigate to="/demo/exams" replace />} />
          <Route path="exams" element={<DemoExams />} />
          <Route path="exams/new" element={<DemoCreateExam />} />
          <Route path="exams/:sessionId" element={<DemoExamDetail />} />
          <Route path="exams/:sessionId/setup" element={<DemoExamSetup />} />
          <Route path="analytics" element={<DemoAnalytics />} />
          <Route path="classes" element={<DemoClasses />} />
          <Route path="members" element={<DemoMembers />} />
          <Route path="costs" element={<DemoCosts />} />
        </Route>

        {/* Mobile PWA routes — self-contained shell, mock data */}
        <Route path="/m" element={<MobileShell />}>
          <Route element={<MobileTabs />}>
            <Route index element={<MExamList />} />
            <Route path="profile" element={<MProfile />} />
          </Route>
          <Route path="exams/new" element={<MExamCreate />} />
          <Route path="exams/:examId/setup" element={<MExamSetup />} />
          <Route path="exams/:examId/review/:paperId" element={<MGradeReview />} />
        </Route>

        {/* Dashboard routes — wrapped in Layout (sidebar + header) */}
        <Route element={<AuthGuard><OrgProvider><Layout /></OrgProvider></AuthGuard>}>
          <Route path="/exams" element={<SessionsPage />} />
          <Route path="/exams/new" element={<Navigate to="/exams-d/new" replace />} />
          <Route path="/exams-ab/new" element={<CreateSessionPage />} />
          <Route path="/exams/:sessionId" element={<SessionDetailPage />} />
          <Route path="/exams/:sessionId/setup" element={<SessionSetupPage />} />
          <Route path="/exams/:sessionId/edit" element={<SessionSetupPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/costs" element={<CostBreakdownPage />} />
          <Route path="/llm-settings" element={<LLMSettingsPage />} />
          <Route path="/admin/stats" element={<AdminStatsPage />} />
          <Route path="/admin/test" element={<AdminTestPage />} />
          <Route path="/admin/packages" element={<AdminPackagesPage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/organizations" element={<Navigate to="/subscribers" replace />} />
          <Route path="/subscribers" element={<SubscribersPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* Mod C exam routes */}
          <Route path="/exams-c/new" element={<ExamCCreatePage />} />
          <Route path="/exams-c/:sessionId/setup" element={<ExamCSetupPage />} />
          <Route path="/exams-c/:sessionId" element={<SessionDetailPage />} />

          {/* Mod D exam routes */}
          <Route path="/exams-d/new" element={<ExamDCreatePage />} />
          <Route path="/exams-d/:sessionId" element={<SessionDetailPage />} />

          {/* Grading Lab */}
          <Route path="/lab" element={<LabPage />} />

          {/* Legacy /sessions/* aliases — keep rendering same components */}
          <Route path="/sessions" element={<Navigate to="/exams" replace />} />
          <Route path="/sessions/new" element={<Navigate to="/exams/new" replace />} />
          <Route path="/sessions/:sessionId" element={<SessionDetailPage />} />
          <Route path="/sessions/:sessionId/setup" element={<SessionSetupPage />} />
        </Route>
      </Routes>
    </>
  )
}

export default App
